import {useState, useEffect, useMemo, useRef, useCallback} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {FileText, Download, RotateCw, Upload} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {ThumbnailSizeControl} from '@/components/shared/ThumbnailSizeControl';
import {useDebounce} from '@/lib/useDebounce';
import {type Submission, type SubmissionsQuery, submissionsApi} from '@/api/submissions';
import {type HopperConfig} from '@/api/hopper-configs';
import {
  useClassificationConfig,
  useLanguages,
  useLanguageSearch,
  useTopics,
  useAllTopics,
  useLocations,
  useSubmissionPolicyViolationCounts,
  useSubmissionStats
} from '@/api/hooks';
import {type Item, ITEM_FLAGS_MAX_IDS, itemsApi} from '@/api/items';
import {useFlagLabel} from '@/components/flags/Flag';
import {ClassifyDialog} from '@/components/investigations/ClassifyDialog';
import {getApiErrorMessage} from '@/api/client';
import {SubmissionScopeToggle} from '@/components/shared/SubmissionScopeToggle';
import {SUBMISSIONS_THUMBNAIL_SIZES, type SubmissionScope, useUiStore} from '@/store/uiStore';
import {
  CreateP0AlertDialog,
  useP0AlertDialog,
  type P0LinkedItem
} from '@/components/p0alerts/CreateP0AlertDialog';
import {
  SubmissionFilters,
  STATUS_OPTIONS,
  TYPE_OPTIONS,
  PLATFORM_STATUS_OPTIONS,
  CUSTOMER_FEEDBACK_OPTIONS,
  flagFilterOptions,
  describeDateRange,
  type MultiSelectOption
} from '@/components/submissions/SubmissionFilters';
import {useSubmissionUrlFilters} from '@/components/submissions/useSubmissionUrlFilters';
import {SubmissionsTable, RemoveSubmissionDialog} from '@/components/submissions/SubmissionsTable';
import {SubmissionDetailSheet} from '@/components/submissions/SubmissionDetailSheet';
import {SubmissionBulkBar} from '@/components/submissions/SubmissionBulkBar';
import {CustomerFeedbackUploadDialog} from '@/components/submissions/CustomerFeedbackUploadDialog';
import {useCustomerFeedbackSources} from '@/api/customerFeedbackSources';
import {PageHeaderActions} from '@/components/layout/PageHeader';
import {SensitiveMediaToggle} from '@/components/shared/SensitiveMedia';
import {TablePanel} from '@/components/ui/table-panel';
import {type FlagColor} from '@/api/flags';
import {showToast} from '@/lib/toast';
import {toneBadge} from '@/lib/status';
import {buildRiskTree, leafIds, topmostSelected} from '@/lib/riskTree';

// Re-exported so existing tests (PlatformStatusCell.test.tsx, SubmissionsWorkspace.test.ts) keep
// importing from this file even though both now live in their own modules.
export {PlatformStatusCell} from '@/components/submissions/SubmissionsTable';
export {findHopperConfig} from '@/components/submissions/findHopperConfig';

type Props = {
  readonly hopperConfigs: HopperConfig[];
  readonly hasBroadView: boolean;
  readonly scope: SubmissionScope;
  readonly onScopeChange: (scope: SubmissionScope) => void;
  // Deep link from a P0 escalation Slack reminder (see P0SlackAlertService.BuildCreateP0Url) - opens
  // CreateP0AlertDialog pre-filled for this one submission directly, without needing it to be visible
  // (or selected) in the filtered table below.
  readonly autoOpenP0ForSubmissionId?: string;
  // Deep link from a dashboard reviewer-comment card: open this submission's detail drawer on
  // arrival. The card also puts the item's external id in ?q, which is what actually narrows the
  // list - this only decides which of the matching rows opens.
  readonly focusSubmissionId?: string;
};

const SUBMISSIONS_PAGE_SIZE = 100;

function downloadCsv(blob: Blob, mode: 'all' | 'current') {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `submissions-${mode}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function ThumbnailSizeSlider() {
  const size = useUiStore((s) => s.submissionsThumbnailSize);
  const setSize = useUiStore((s) => s.setSubmissionsThumbnailSize);

  return (
    <ThumbnailSizeControl
      min={0}
      max={SUBMISSIONS_THUMBNAIL_SIZES.length - 1}
      value={SUBMISSIONS_THUMBNAIL_SIZES.indexOf(size)}
      sliderClassName="w-16"
      onChange={(index) => {
        const level = SUBMISSIONS_THUMBNAIL_SIZES[index];
        if (level) {
          setSize(level);
        }
      }}
    />
  );
}

// The value of the "All" tab in the policy-violation strip - a sentinel no vertical name can be.
const ALL_POLICY_VIOLATIONS = '__all__';

export function SubmissionsWorkspace({
  hopperConfigs,
  focusSubmissionId,
  hasBroadView,
  scope,
  onScopeChange,
  autoOpenP0ForSubmissionId
}: Props) {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [exportingMode, setExportingMode] = useState<'all' | 'current' | null>(null);
  const exporting = exportingMode !== null;
  const [exportError, setExportError] = useState<string | null>(null);
  const [feedbackUploadOpen, setFeedbackUploadOpen] = useState(false);
  const feedbackSources = useCustomerFeedbackSources(hasBroadView);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const p0Dialog = useP0AlertDialog();
  const [autoOpenLinkedItem, setAutoOpenLinkedItem] = useState<P0LinkedItem | null>(null);

  // Which submission's detail drawer is open, if any - a single piece of state now that the table
  // is one flat list rather than one expandable row per policy-violation group.
  const [openSubmissionId, setOpenSubmissionId] = useState<string | null>(null);
  const autoOpenedFocusRef = useRef(false);

  // The submission a Resubmit row action opened ClassifyDialog against - non-null drives both the
  // dialog's open state and its resubmitOf prop. Loaded here rather than by the table itself so the
  // dialog mounts once at this level, same as CreateP0AlertDialog below.
  const [resubmitTarget, setResubmitTarget] = useState<Submission | null>(null);
  const {data: resubmitClassifications} = useClassificationConfig();
  const {data: resubmitLanguages} = useLanguages();
  const {data: resubmitTopics} = useTopics();
  const {data: resubmitLocations} = useLocations();
  // Bumped after a successful resubmit to re-trigger the filter-driven fetch below, since a
  // resubmission's own SubmissionResponse row (fetched fresh from page 1) is the only source for
  // its ordinal, delivery state, etc. - useCreateClassification's response carries only ids.
  const [reloadNonce, setReloadNonce] = useState(0);

  // The submission a Remove row action opened the confirmation dialog for - a single instance owned
  // here rather than one mounted per row.
  const [removeTarget, setRemoveTarget] = useState<Submission | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // Deep link from a P0 escalation Slack reminder - fetches just enough about this one submission to
  // open CreateP0AlertDialog for it directly, without needing it to be loaded/selected in the table
  // below (whatever filters happen to be active). Best-effort: if the lookup fails (e.g. the
  // submission was since removed), it just doesn't auto-open rather than erroring the whole page.
  useEffect(() => {
    if (!autoOpenP0ForSubmissionId) {
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const history = await submissionsApi.history(autoOpenP0ForSubmissionId);
        if (cancelled) {
          return;
        }

        setAutoOpenLinkedItem({
          id: autoOpenP0ForSubmissionId,
          submissionType: history.submission_type,
          policyViolation: history.policy_violation,
          displayName:
            history.item?.title ??
            history.item?.url ??
            history.item_external_id ??
            autoOpenP0ForSubmissionId,
          externalId: history.item_external_id ?? ''
        });
        p0Dialog.setOpen(true);
      } catch {
        // Best-effort deep link - nothing to recover from client-side.
      }
    })();

    return () => {
      cancelled = true;
    };
    // Only re-run if the target submission id itself changes - p0Dialog's own identity is stable
    // across renders (useP0AlertDialog memoizes it) but isn't itself a dependency we want to react to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenP0ForSubmissionId]);

  // Search and every filter live in the URL (see useSubmissionUrlFilters), so a view survives a
  // reload and can be linked to.
  const {
    query,
    setQuery,
    debouncedQuery,
    policyViolationFilter,
    setPolicyViolationFilter,
    statusFilter,
    setStatusFilter,
    submissionTypeFilter,
    setSubmissionTypeFilter,
    analystFilter,
    setAnalystFilter,
    languageFilter,
    setLanguageFilter,
    topicFilter,
    setTopicFilter,
    locationFilter,
    setLocationFilter,
    // Risks under a policy violation (e.g. CSAM, Grooming) - what the Classification column shows,
    // distinct from the policy-violation tabs above.
    riskFilter,
    setRiskFilter,
    platformStatusFilter,
    setPlatformStatusFilter,
    customerFeedbackFilter,
    setCustomerFeedbackFilter,
    flagFilter,
    setFlagFilter,
    // Undefined = no opinion (Any), matching the tri-state Any/Yes/No control these drive.
    hasCalibrationFilter,
    setHasCalibrationFilter,
    hasCalibrationNotesFilter,
    setHasCalibrationNotesFilter,
    hasReviewerCommentsFilter,
    setHasReviewerCommentsFilter,
    thumbnailChangedFilter,
    setThumbnailChangedFilter,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    clearDates,
    clearAll: clearAllFilters
  } = useSubmissionUrlFilters(focusSubmissionId);

  // The tiles are fetched here rather than by SubmissionsPage because they have to follow this
  // component's own date filter - a header reading "1,000 total" over 642 visible rows is the kind
  // of mismatch analysts stop trusting. Mirrors the page's own viewAll: its `scope` prop is already
  // 'all' whenever the hopper-tile override is in play.
  const {data: statsData} = useSubmissionStats(
    hasBroadView || scope === 'all',
    undefined,
    dateFrom,
    dateTo
  );
  const stats = statsData ?? null;

  const {data: languageOptions} = useLanguages();
  // Retired topics included: this is a filter over history, and a submission tagged with a topic
  // that has since been archived, renamed or run past its end date must stay findable.
  const {data: topicOptions} = useAllTopics();
  const {data: locationOptions} = useLocations();

  // The default useLanguages() call only returns the default-visible subset (~184 entries with an
  // ISO 639-1 code) - the full curated ISO 639-3 list (~7,000) is only reachable by searching,
  // server-side, the same way the classify form's own language picker reaches it (see LanguagePicker
  // in components/classify/EnrichmentForm.tsx).
  const [languageSearchTerm, setLanguageSearchTerm] = useState('');
  const debouncedLanguageSearchTerm = useDebounce(languageSearchTerm, 300);
  const {data: languageSearchResults, isFetching: languageSearching} = useLanguageSearch(
    debouncedLanguageSearchTerm
  );

  // Shared between SubmissionFilters' own options props and the active-filter chip bar below, so a
  // chip's label always matches what the dropdown showed (e.g. "in hopper" -> "In Hopper", analyst
  // emails -> their name-part) instead of falling back to the raw filter value.
  const policyViolationOptions = useMemo<MultiSelectOption[]>(
    () =>
      (stats?.by_policy_violation ?? []).map((v) => ({
        value: v.policy_violation,
        label: v.policy_violation
      })),
    [stats]
  );
  const analystOptions = useMemo<MultiSelectOption[]>(
    () =>
      (stats?.analysts ?? []).map((email) => ({value: email, label: email.split('@')[0] ?? email})),
    [stats]
  );
  // A one-character term is below the search endpoint's threshold (see useLanguageSearch) - narrow
  // the preloaded default-visible set locally rather than showing everything while still typing.
  const languageMultiOptions = useMemo<MultiSelectOption[]>(() => {
    const trimmed = debouncedLanguageSearchTerm.trim();
    const source =
      trimmed.length >= 2
        ? (languageSearchResults ?? [])
        : trimmed.length === 1
          ? (languageOptions ?? []).filter((item) =>
              item.name.toLowerCase().includes(trimmed.toLowerCase())
            )
          : (languageOptions ?? []);
    const options = source.map((item) => ({value: item.name, label: item.name}));
    // A search term narrows `source` to the server's/local match set, which can exclude a language
    // the analyst already picked earlier (outside the current search) - without this, an active
    // search would make an already-checked language vanish from the list entirely (it would still
    // show as a chip below, but with no way to see it's checked or uncheck it from here).
    const missingSelected = languageFilter
      .filter((value) => !options.some((option) => option.value === value))
      .map((value) => ({value, label: value}));
    return [...options, ...missingSelected];
  }, [debouncedLanguageSearchTerm, languageSearchResults, languageOptions, languageFilter]);
  const topicMultiOptions = useMemo<MultiSelectOption[]>(
    () => (topicOptions ?? []).map((item) => ({value: item.name, label: item.name})),
    [topicOptions]
  );
  const locationMultiOptions = useMemo<MultiSelectOption[]>(
    () => (locationOptions ?? []).map((item) => ({value: item.name, label: item.name})),
    [locationOptions]
  );
  const labelOf = useFlagLabel();
  const flagOptions = useMemo(() => flagFilterOptions(labelOf), [labelOf]);
  const riskTree = useMemo(
    () => buildRiskTree(resubmitClassifications ?? []),
    [resubmitClassifications]
  );

  const filterParams = useMemo<SubmissionsQuery>(() => {
    const params: SubmissionsQuery = {};
    if (debouncedQuery) {
      params.q = debouncedQuery;
    }

    if (policyViolationFilter.length > 0) {
      params.policy_violation = policyViolationFilter;
    }

    if (statusFilter.length > 0) {
      params.action_status = statusFilter;
    }

    if (submissionTypeFilter.length > 0) {
      params.submission_type = submissionTypeFilter;
    }

    if (analystFilter.length > 0) {
      params.analyst = analystFilter;
    }

    if (languageFilter.length > 0) {
      params.language = languageFilter;
    }

    if (topicFilter.length > 0) {
      params.topic = topicFilter;
    }

    if (locationFilter.length > 0) {
      params.location = locationFilter;
    }

    if (riskFilter.length > 0) {
      params.risk_node = topmostSelected(riskTree, riskFilter);
    }

    if (platformStatusFilter.length > 0) {
      params.platform_status = platformStatusFilter;
    }

    if (customerFeedbackFilter.length > 0) {
      params.customer_feedback = customerFeedbackFilter;
    }

    if (flagFilter.length > 0) {
      params.flag = flagFilter;
    }

    if (hasCalibrationFilter !== undefined) {
      params.has_calibration = hasCalibrationFilter;
    }

    if (hasCalibrationNotesFilter !== undefined) {
      params.has_calibration_notes = hasCalibrationNotesFilter;
    }

    if (hasReviewerCommentsFilter !== undefined) {
      params.has_reviewer_comments = hasReviewerCommentsFilter;
    }

    if (thumbnailChangedFilter !== undefined) {
      params.thumbnail_changed = thumbnailChangedFilter;
    }

    if (dateFrom) {
      params.date_from = dateFrom;
    }

    if (dateTo) {
      params.date_to = dateTo;
    }

    if (!hasBroadView && scope === 'all') {
      params.view_all = true;
    }

    return params;
  }, [
    debouncedQuery,
    policyViolationFilter,
    statusFilter,
    submissionTypeFilter,
    analystFilter,
    languageFilter,
    topicFilter,
    locationFilter,
    riskFilter,
    riskTree,
    platformStatusFilter,
    customerFeedbackFilter,
    flagFilter,
    hasCalibrationFilter,
    hasCalibrationNotesFilter,
    hasReviewerCommentsFilter,
    thumbnailChangedFilter,
    dateFrom,
    dateTo,
    hasBroadView,
    scope
  ]);

  // One removable chip per active filter value, so it's obvious at a glance what's currently
  // narrowing the list without opening every dropdown to check - and each is independently
  // removable, not just an all-or-nothing "clear filters" action.
  const activeFilterChips = useMemo(() => {
    const chips: Array<{key: string; label: string; onRemove: () => void}> = [];

    function multiValueChips(
      values: string[],
      options: MultiSelectOption[],
      prefix: string,
      onChange: (next: string[]) => void
    ) {
      for (const value of values) {
        chips.push({
          key: `${prefix}:${value}`,
          label: options.find((option) => option.value === value)?.label ?? value,
          onRemove() {
            onChange(values.filter((v) => v !== value));
          }
        });
      }
    }

    if (query) {
      chips.push({
        key: 'q',
        label: `"${query}"`,
        onRemove() {
          setQuery('');
        }
      });
    }

    multiValueChips(
      policyViolationFilter,
      policyViolationOptions,
      'policy_violation',
      setPolicyViolationFilter
    );
    multiValueChips(statusFilter, STATUS_OPTIONS, 'status', setStatusFilter);
    multiValueChips(submissionTypeFilter, TYPE_OPTIONS, 'type', setSubmissionTypeFilter);
    multiValueChips(analystFilter, analystOptions, 'analyst', setAnalystFilter);
    multiValueChips(languageFilter, languageMultiOptions, 'language', setLanguageFilter);
    multiValueChips(topicFilter, topicMultiOptions, 'topic', setTopicFilter);
    multiValueChips(locationFilter, locationMultiOptions, 'location', setLocationFilter);
    // A fully selected branch is one chip carrying its path, not a chip per leaf.
    for (const id of topmostSelected(riskTree, riskFilter)) {
      const node = riskTree.byId.get(id);
      const covered = node ? leafIds(node) : [id];
      chips.push({
        key: `risk:${id}`,
        label: node?.path ?? id,
        onRemove() {
          setRiskFilter(riskFilter.filter((v) => !covered.includes(v)));
        }
      });
    }

    multiValueChips(
      platformStatusFilter,
      PLATFORM_STATUS_OPTIONS,
      'platform_status',
      setPlatformStatusFilter
    );
    multiValueChips(
      customerFeedbackFilter,
      CUSTOMER_FEEDBACK_OPTIONS,
      'customer_feedback',
      setCustomerFeedbackFilter
    );
    multiValueChips(flagFilter, flagOptions, 'flag', setFlagFilter);
    if (hasCalibrationFilter !== undefined) {
      chips.push({
        key: 'has_calibration',
        label: hasCalibrationFilter ? 'Calibrated' : 'Not calibrated',
        onRemove() {
          setHasCalibrationFilter(undefined);
        }
      });
    }

    if (hasCalibrationNotesFilter !== undefined) {
      chips.push({
        key: 'has_calibration_notes',
        label: hasCalibrationNotesFilter ? 'Has calibration notes' : 'No calibration notes',
        onRemove() {
          setHasCalibrationNotesFilter(undefined);
        }
      });
    }

    if (hasReviewerCommentsFilter !== undefined) {
      chips.push({
        key: 'has_reviewer_comments',
        label: hasReviewerCommentsFilter ? 'Has reviewer comments' : 'No reviewer comments',
        onRemove() {
          setHasReviewerCommentsFilter(undefined);
        }
      });
    }

    if (thumbnailChangedFilter !== undefined) {
      chips.push({
        key: 'thumbnail_changed',
        label: thumbnailChangedFilter ? 'Thumbnail changed' : 'Thumbnail unchanged',
        onRemove() {
          setThumbnailChangedFilter(undefined);
        }
      });
    }

    if (dateFrom || dateTo) {
      chips.push({
        key: 'date',
        label: describeDateRange(dateFrom, dateTo),
        onRemove: clearDates
      });
    }

    return chips;
  }, [
    query,
    policyViolationFilter,
    policyViolationOptions,
    statusFilter,
    submissionTypeFilter,
    analystFilter,
    analystOptions,
    languageFilter,
    languageMultiOptions,
    topicFilter,
    topicMultiOptions,
    locationFilter,
    locationMultiOptions,
    riskFilter,
    riskTree,
    platformStatusFilter,
    customerFeedbackFilter,
    flagFilter,
    flagOptions,
    hasCalibrationFilter,
    hasCalibrationNotesFilter,
    hasReviewerCommentsFilter,
    thumbnailChangedFilter,
    dateFrom,
    dateTo,
    // The URL setters change with the params; listed so a chip never writes over a newer URL.
    setQuery,
    setPolicyViolationFilter,
    setStatusFilter,
    setSubmissionTypeFilter,
    setAnalystFilter,
    setLanguageFilter,
    setTopicFilter,
    setLocationFilter,
    setRiskFilter,
    setPlatformStatusFilter,
    setCustomerFeedbackFilter,
    setFlagFilter,
    setHasCalibrationFilter,
    setHasCalibrationNotesFilter,
    setHasReviewerCommentsFilter,
    setThumbnailChangedFilter,
    clearDates
  ]);

  // The vertical tabs count under every other active filter (GT-16827) - fetched with the vertical
  // itself dropped, so the key doesn't change on a tab click and a picked tab's siblings keep
  // saying what picking them would show. The tab set itself still comes from stats, so a vertical
  // the filters have emptied stays visible at 0 rather than vanishing from under the cursor.
  const countParams = useMemo(() => {
    const {policy_violation: _ignored, ...rest} = filterParams;
    return rest;
  }, [filterParams]);
  const {data: policyViolationCounts} = useSubmissionPolicyViolationCounts(countParams);
  const countByPolicyViolation = useMemo(
    () => new Map((policyViolationCounts ?? []).map((c) => [c.policy_violation, c.count])),
    [policyViolationCounts]
  );
  const countFor = (policyViolation: string, fallback: number) =>
    policyViolationCounts ? (countByPolicyViolation.get(policyViolation) ?? 0) : fallback;
  const allCount = policyViolationCounts
    ? policyViolationCounts.reduce((sum, c) => sum + c.count, 0)
    : (stats?.total ?? 0);

  // Live pointer to the latest filterParams, so an in-flight loadMore (whose own closure only ever
  // sees the filterParams snapshot from when it started) can detect a filter change that happened
  // while its request was in flight.
  const filterParamsRef = useRef(filterParams);
  useEffect(() => {
    filterParamsRef.current = filterParams;
  }, [filterParams]);

  // A selection is only meaningful for the list it was made on.
  useEffect(() => {
    setSelected(new Set());
  }, [filterParams]);

  // Reset to page 1 and refetch fresh whenever any filter changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setLoadMoreFailed(false);
    submissionsApi
      .list({page: 1, page_size: SUBMISSIONS_PAGE_SIZE, ...filterParams})
      .then((data) => {
        if (cancelled) {
          return;
        }

        setSubmissions(data.results);
        setTotalCount(data.total_count);
        setPage(1);
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }

        setSubmissions([]);
        setTotalCount(0);
        setLoadError(getApiErrorMessage(error, 'Failed to load submissions.'));
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [filterParams, reloadNonce]);

  // Opens the deep-linked submission's detail drawer as soon as it shows up in the loaded page -
  // once only, so an analyst who explicitly closes it isn't fought back into it on the next refetch.
  useEffect(() => {
    if (autoOpenedFocusRef.current || !focusSubmissionId) {
      return;
    }

    if (submissions.some((s) => s.id === focusSubmissionId)) {
      setOpenSubmissionId(focusSubmissionId);
      autoOpenedFocusRef.current = true;
    }
  }, [submissions, focusSubmissionId]);

  // Reconstructs just enough of an Item for ClassifyDialog to submit against. resubmitOf mode
  // skips ClassificationTree's clash check on first render (it seeds resubmitSource directly from
  // resubmitOf), but that mode IS reachable from Classify again - the Enrich footer's Back button
  // and the header's Change link both step back there - and re-picking the same vertical clears
  // resubmitSource, relying on hasLiveSubmissionIn to re-detect the clash and set it again. A
  // single group naming this submission's own vertical/status is seeded here (rather than left
  // empty) so that re-detection has something to match against instead of silently losing
  // resubmit:true and 409ing with no way forward but closing the dialog.
  //
  // Returns the array itself (not a bare Item, wrapped at the JSX call site) so its identity stays
  // stable across renders that don't change resubmitTarget - e.g. react-query's window-focus
  // refetch. ClassifyDialog's items prop is a dep of its own "reset per-group state" effect, and
  // that effect's resubmitOf branch calls setEnrichment/setStep on every fire - a fresh [] or
  // [item] literal at the JSX call site re-triggered it on every incidental parent re-render,
  // silently reverting whatever the analyst had already amended mid-edit.
  const resubmitItems: Item[] = useMemo(() => {
    if (!resubmitTarget?.item_id) {
      return [];
    }

    return [
      {
        id: resubmitTarget.item_id,
        external_id: resubmitTarget.item_external_id ?? '',
        platform: resubmitTarget.platform,
        // What a resubmit is about: the account, a Pinterest pin, or a YouTube video.
        content_type:
          resubmitTarget.submission_type === 'Actor'
            ? 'actor'
            : resubmitTarget.platform === 'pinterest'
              ? 'pin'
              : 'video',
        title: resubmitTarget.display_name,
        url: resubmitTarget.source_url,
        thumbnail_url: null,
        author_handle: resubmitTarget.handle,
        author_id: null,
        author_name: null,
        published_at: null,
        metadata: {},
        affiliate: resubmitTarget.affiliate,
        created_at: resubmitTarget.created_at,
        submission_status: null,
        submission_policy_violations: [],
        submission_status_groups: [
          {
            status: resubmitTarget.action_status,
            policy_violations: [resubmitTarget.policy_violation],
            submitted_at: null
          }
        ],
        item_status: null,
        is_new_item: null
      }
    ];
  }, [resubmitTarget]);

  const markSent = useCallback((ids: string[]) => {
    const idSet = new Set(ids);
    setSubmissions((previous) =>
      previous.map((s) => (idSet.has(s.id) ? {...s, action_status: 'sent to client' as const} : s))
    );
    // Sent submissions dropping out of the selection mirrors the old per-group behaviour of
    // toggling them off once fired - there's nothing further to do with them selected.
    setSelected((previous) => {
      const next = new Set(previous);
      for (const id of ids) {
        next.delete(id);
      }

      return next;
    });
  }, []);

  const markRetried = useCallback((id: string) => {
    setSubmissions((previous) =>
      previous.map((s) => (s.id === id ? {...s, action_status: 'in hopper' as const} : s))
    );
  }, []);

  // Flips a row's "at a glance" badge the moment the drawer action that earns it succeeds, rather
  // than leaving it stale until the next full list refetch - see SubmissionDetailSheet's own comment.
  const markCalibrated = useCallback((id: string, completed: boolean) => {
    setSubmissions((previous) =>
      previous.map((s) =>
        s.id === id
          ? {...s, has_calibration_notes: true, has_calibration: s.has_calibration || completed}
          : s
      )
    );
    setReloadNonce((previous) => previous + 1);
  }, []);

  const markHasReviewerComments = useCallback((id: string, value = true) => {
    setSubmissions((previous) =>
      previous.map((s) => (s.id === id ? {...s, has_reviewer_comments: value} : s))
    );
  }, []);

  const markRemoved = useCallback((id: string) => {
    setSubmissions((previous) => previous.filter((s) => s.id !== id));
    setTotalCount((previous) => Math.max(0, previous - 1));
    setSelected((previous) => {
      if (!previous.has(id)) {
        return previous;
      }

      const next = new Set(previous);
      next.delete(id);
      return next;
    });
    setOpenSubmissionId((previous) => (previous === id ? null : previous));
  }, []);

  const toggleRow = useCallback((id: string) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((previous) =>
      submissions.every((s) => previous.has(s.id))
        ? new Set()
        : new Set(submissions.map((s) => s.id))
    );
  }, [submissions]);

  const queryClient = useQueryClient();

  // Flags live on items only, so this flags the rows' items and then every loaded row of those items
  // - an item submitted under two verticals shows one flag on both. Updates the rows in place rather
  // than refetching, so a row flagged under an active flag filter stays put until the next refresh
  // instead of vanishing from under the cursor mid-triage. The tab counts are a separate query, so
  // they are refetched to follow.
  const handleSetFlag = useCallback(
    async (rows: readonly Submission[], color: FlagColor | null) => {
      const itemIds = [
        ...new Set(rows.flatMap((s) => (s.is_historic || !s.item_id ? [] : [s.item_id])))
      ];
      if (itemIds.length === 0) {
        return;
      }

      try {
        for (let start = 0; start < itemIds.length; start += ITEM_FLAGS_MAX_IDS) {
          await itemsApi.setFlags(itemIds.slice(start, start + ITEM_FLAGS_MAX_IDS), color);
        }

        const itemIdSet = new Set(itemIds);
        setSubmissions((previous) =>
          previous.map((s) =>
            s.item_id && !s.is_historic && itemIdSet.has(s.item_id) ? {...s, flag: color} : s
          )
        );
        if (flagFilter.length > 0) {
          await queryClient.invalidateQueries({
            queryKey: ['submissions', 'policy-violation-counts']
          });
        }
      } catch (error) {
        showToast(getApiErrorMessage(error, 'Failed to update flag'), 'error');
      }
    },
    [flagFilter, queryClient]
  );

  const selectedSubmissions = useMemo(
    () => submissions.filter((s) => selected.has(s.id)),
    [submissions, selected]
  );

  const openSubmission = useMemo(
    () => submissions.find((s) => s.id === openSubmissionId) ?? null,
    [submissions, openSubmissionId]
  );

  function openP0Dialog() {
    p0Dialog.setOpen(true);
  }

  const p0LinkedItems: P0LinkedItem[] = selectedSubmissions.map((s) => ({
    id: s.id,
    submissionType: s.submission_type,
    policyViolation: s.policy_violation,
    displayName: s.display_name ?? s.handle ?? s.content_id,
    externalId: s.item_external_id ?? s.content_id,
    handle: s.handle
  }));

  const hasMore = submissions.length < totalCount;

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    const requestFilterParams = filterParams;
    try {
      const nextPage = page + 1;
      const data = await submissionsApi.list({
        page: nextPage,
        page_size: SUBMISSIONS_PAGE_SIZE,
        ...requestFilterParams
      });
      // Discard the response if filters changed while this request was in flight.
      if (filterParamsRef.current === requestFilterParams) {
        setSubmissions((previous) => [...previous, ...data.results]);
        setTotalCount(data.total_count);
        setPage(nextPage);
      }
    } catch {
      // Stop auto-loading on failure instead of hammering the API every time the
      // still-visible sentinel re-fires; a filter change (which refetches page 1) clears this.
      setLoadMoreFailed(true);
    } finally {
      setLoadingMore(false);
    }
  }, [page, filterParams]);

  const submissionsSentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sentinel = submissionsSentinelRef.current;
    if (!sentinel || !hasMore || loading || loadMoreFailed) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !loadingMore) {
        void loadMore();
      }
    });
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [hasMore, loading, loadingMore, loadMoreFailed, loadMore]);

  const [refreshing, setRefreshing] = useState(false);

  // Re-fetches page 1 with the current filters in place, without resetting scroll or closing
  // whichever detail drawer is already open.
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setLoadError(null);
    try {
      const data = await submissionsApi.list({
        page: 1,
        page_size: SUBMISSIONS_PAGE_SIZE,
        ...filterParams
      });
      setSubmissions(data.results);
      setTotalCount(data.total_count);
      setPage(1);
      setLoadMoreFailed(false);
    } catch (error) {
      setLoadError(getApiErrorMessage(error, 'Failed to refresh submissions.'));
    } finally {
      setRefreshing(false);
    }
  }, [filterParams]);

  // Both modes are resolved server-side from the current filters, so the caller's allowed verticals
  // apply either way. 'all' is every match, not just the pages loaded; 'current' is the first
  // `submissions.length` rows of the same ordering, i.e. what the grid is showing.
  const handleExport = useCallback(
    async (mode: 'all' | 'current') => {
      setExportingMode(mode);
      setExportError(null);
      try {
        const blob = await submissionsApi.exportCsv(
          mode === 'current' ? {...filterParams, max_rows: submissions.length} : filterParams
        );
        downloadCsv(blob, mode);
      } catch (error) {
        setExportError(getApiErrorMessage(error, 'Export failed.'));
      } finally {
        setExportingMode(null);
      }
    },
    [filterParams, submissions.length]
  );

  async function handleRemoveConfirm(reason: string) {
    if (!removeTarget) {
      return;
    }

    setRemoving(true);
    setRemoveError(null);
    try {
      await submissionsApi.remove(removeTarget.id, reason);
      markRemoved(removeTarget.id);
      setRemoveTarget(null);
    } catch (error) {
      setRemoveError(getApiErrorMessage(error, 'Remove failed'));
    } finally {
      setRemoving(false);
    }
  }

  const activePolicyViolationTab = policyViolationFilter[0] ?? ALL_POLICY_VIOLATIONS;

  // No fill of its own: the page takes the shell's background, like the other table pages, so the
  // white filter bar and table panel are what stand out against it.
  return (
    <div className="text-foreground flex h-full w-full flex-col">
      <PageHeaderActions>
        {hasBroadView ? null : <SubmissionScopeToggle scope={scope} onChange={onScopeChange} />}
        <Button
          variant="outline"
          size="icon-sm"
          disabled={refreshing || loading}
          aria-label="Refresh"
          onClick={() => void handleRefresh()}>
          {refreshing ? <Spinner /> : <RotateCw />}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={exporting || submissions.length === 0}
          onClick={() => void handleExport('current')}>
          {exportingMode === 'current' ? <Spinner /> : <Download />}
          {exportingMode === 'current'
            ? 'Exporting…'
            : `Export current as CSV (${submissions.length.toLocaleString()})`}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={exporting || totalCount === 0}
          onClick={() => void handleExport('all')}>
          {exportingMode === 'all' ? <Spinner /> : <Download />}
          {exportingMode === 'all'
            ? 'Exporting…'
            : `Export all as CSV (${totalCount.toLocaleString()})`}
        </Button>
        {/* analyst_admin/admin only - the API refuses everyone else (Roles.BroadView). */}
        {hasBroadView ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setFeedbackUploadOpen(true);
            }}>
            <Upload />
            Customer feedback
            {/* A fetched sheet has changes no one has confirmed yet. */}
            {feedbackSources.data?.sources.some((s) => s.has_pending) ? (
              <Badge {...toneBadge('info')}>To review</Badge>
            ) : null}
          </Button>
        ) : null}
      </PageHeaderActions>

      {/* The filter bar sits outside the scroll region on the card surface, the same strip
          Investigations puts its filters in, so the controls stay put while the table scrolls. */}
      <div className="bg-card border-border shrink-0 space-y-3 border-b px-5 py-3">
        {/* Policy Violation Tabs */}
        {stats && stats.by_policy_violation.length > 0 ? (
          <Tabs
            value={activePolicyViolationTab}
            onValueChange={(value) => {
              setPolicyViolationFilter(
                value === ALL_POLICY_VIOLATIONS || typeof value !== 'string' ? [] : [value]
              );
            }}>
            <TabsList className="h-auto max-w-full flex-wrap justify-start gap-1 group-data-horizontal/tabs:h-auto">
              <TabsTrigger value={ALL_POLICY_VIOLATIONS} className="h-7 flex-none">
                All
                <Badge variant="secondary" className="h-4 px-1">
                  {allCount}
                </Badge>
              </TabsTrigger>
              {stats.by_policy_violation.map((v) => (
                <TabsTrigger
                  key={v.policy_violation}
                  value={v.policy_violation}
                  className="h-7 flex-none">
                  {v.policy_violation}
                  <Badge variant="secondary" className="h-4 px-1">
                    {countFor(v.policy_violation, v.total)}
                  </Badge>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        ) : null}

        {/* Search, Status, Date range, Flags and the Filters popover, with the visible count */}
        <div className="flex flex-wrap items-center gap-2">
          {/* min-w, not min-w-0: with a zero floor flex-1 shrinks this to nothing beside the count and
              toggles, and its wrapped controls spill underneath them instead of the row wrapping. */}
          <div className="min-w-72 flex-1">
            <SubmissionFilters
              query={query}
              statusFilter={statusFilter}
              dateFrom={dateFrom}
              dateTo={dateTo}
              submissionTypeFilter={submissionTypeFilter}
              analystFilter={analystFilter}
              analystOptions={analystOptions}
              languageFilter={languageFilter}
              languageOptions={languageMultiOptions}
              languageSearching={languageSearching}
              topicFilter={topicFilter}
              topicOptions={topicMultiOptions}
              locationFilter={locationFilter}
              locationOptions={locationMultiOptions}
              riskFilter={riskFilter}
              riskTree={riskTree}
              platformStatusFilter={platformStatusFilter}
              customerFeedbackFilter={customerFeedbackFilter}
              hasCalibrationFilter={hasCalibrationFilter}
              hasCalibrationNotesFilter={hasCalibrationNotesFilter}
              hasReviewerCommentsFilter={hasReviewerCommentsFilter}
              thumbnailChangedFilter={thumbnailChangedFilter}
              flagFilter={flagFilter}
              onQueryChange={setQuery}
              onStatusChange={setStatusFilter}
              onDateFromChange={setDateFrom}
              onDateToChange={setDateTo}
              onFlagChange={setFlagFilter}
              onSubmissionTypeChange={setSubmissionTypeFilter}
              onAnalystChange={setAnalystFilter}
              onLanguageChange={setLanguageFilter}
              onLanguageSearchChange={setLanguageSearchTerm}
              onTopicChange={setTopicFilter}
              onLocationChange={setLocationFilter}
              onRiskChange={setRiskFilter}
              onPlatformStatusChange={setPlatformStatusFilter}
              onCustomerFeedbackChange={setCustomerFeedbackFilter}
              onHasCalibrationChange={setHasCalibrationFilter}
              onHasCalibrationNotesChange={setHasCalibrationNotesFilter}
              onHasReviewerCommentsChange={setHasReviewerCommentsFilter}
              onThumbnailChangedChange={setThumbnailChangedFilter}
            />
          </div>
          <p className="text-muted-foreground ml-auto text-xs whitespace-nowrap">
            {loading
              ? 'Loading…'
              : `${submissions.length.toLocaleString()} of ${totalCount.toLocaleString()}`}
            {stats
              ? ` · ${stats.sent.toLocaleString()} sent · ${stats.in_hopper.toLocaleString()} in hopper`
              : ''}
          </p>
          {/* How the table's thumbnails look, so they sit with the filters rather than the header. */}
          <SensitiveMediaToggle />
          <ThumbnailSizeSlider />
        </div>

        {/* Active Filter Chips */}
        {activeFilterChips.length > 0 ? (
          <div
            className="flex flex-wrap items-center gap-2"
            role="group"
            aria-label="Active filters">
            {activeFilterChips.map((chip) => (
              <Button
                key={chip.key}
                variant="secondary"
                size="xs"
                aria-label={`Remove ${chip.label} filter`}
                onClick={chip.onRemove}>
                {chip.label}
                <span aria-hidden="true">×</span>
              </Button>
            ))}
            <Button variant="link" size="xs" onClick={clearAllFilters}>
              Clear all
            </Button>
          </div>
        ) : null}
      </div>

      {/* No max-w cap, unlike the card/tile pages (QC, Queues, Dashboard) - this page's whole point
          is a wide data table, and Investigations, the other dense-grid page, is full width too.
          This region doesn't scroll: the table panel fills what's left of it and scrolls itself, so
          its header stays pinned and there is one scrollbar, not one inside another. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 px-5 py-4">
        {exportError ? (
          <Alert variant="destructive">
            <AlertTitle>Export failed</AlertTitle>
            <AlertDescription>{exportError}</AlertDescription>
          </Alert>
        ) : null}

        {/* List Content Area */}
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Spinner className="text-muted-foreground size-6" />
          </div>
        ) : loadError ? (
          <Alert variant="destructive">
            <AlertTitle>Failed to load submissions</AlertTitle>
            <AlertDescription>{loadError}</AlertDescription>
          </Alert>
        ) : submissions.length === 0 ? (
          <Empty className="border-border h-64 border">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <FileText />
              </EmptyMedia>
              <EmptyTitle>No Submissions Found</EmptyTitle>
              <EmptyDescription>
                No content submissions match your current search query or filters.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <TablePanel className="flex min-h-0 flex-col">
            <SubmissionsTable
              submissions={submissions}
              selected={selected}
              openSubmissionId={openSubmissionId}
              footer={
                // Infinite scroll trigger. It lives inside the table's own scroll region - the
                // only one on the page - so it comes into view when the table is scrolled to its end.
                hasMore && loadMoreFailed ? (
                  <p className="text-destructive-ink py-4 text-center text-xs">
                    Failed to load more submissions. Adjust a filter to retry.
                  </p>
                ) : hasMore ? (
                  <div ref={submissionsSentinelRef} className="flex justify-center py-4">
                    {loadingMore ? <Spinner className="text-muted-foreground" /> : null}
                  </div>
                ) : null
              }
              onToggleRow={toggleRow}
              onToggleAll={toggleAll}
              onOpenDetail={(sub) => {
                setOpenSubmissionId(sub.id);
              }}
              onSetFlag={(rows, color) => void handleSetFlag(rows, color)}
              onRetried={markRetried}
              onRequestRemove={setRemoveTarget}
              onResubmit={setResubmitTarget}
            />
          </TablePanel>
        )}
      </div>

      <SubmissionBulkBar
        selectedSubmissions={selectedSubmissions}
        hopperConfigs={hopperConfigs}
        onSent={markSent}
        onClear={() => {
          setSelected(new Set());
        }}
        onCreateP0={openP0Dialog}
        onSetFlag={(color) => void handleSetFlag(selectedSubmissions, color)}
      />

      <SubmissionDetailSheet
        sub={openSubmission}
        onOpenChange={(open) => {
          if (!open) {
            setOpenSubmissionId(null);
          }
        }}
        onCalibrated={markCalibrated}
        onCommentAdded={markHasReviewerComments}
        onLastCommentDeleted={(id) => {
          markHasReviewerComments(id, false);
        }}
      />

      <RemoveSubmissionDialog
        open={removeTarget !== null}
        submitting={removing}
        error={removeError}
        onCancel={() => {
          setRemoveTarget(null);
          setRemoveError(null);
        }}
        onConfirm={(reason) => void handleRemoveConfirm(reason)}
      />

      <CreateP0AlertDialog
        {...p0Dialog.dialogProps(autoOpenLinkedItem ? [autoOpenLinkedItem] : p0LinkedItems, {
          onCreated() {
            setSelected(new Set());
            setAutoOpenLinkedItem(null);
          }
        })}
        onOpenChange={(next) => {
          p0Dialog.setOpen(next);
          if (!next) {
            setAutoOpenLinkedItem(null);
          }
        }}
      />

      {feedbackUploadOpen ? (
        <CustomerFeedbackUploadDialog
          onClose={() => {
            setFeedbackUploadOpen(false);
          }}
          onApplied={(result) => {
            setFeedbackUploadOpen(false);
            setReloadNonce((n) => n + 1);
            showToast(
              `Customer feedback saved: ${result.new.toLocaleString()} new, ${result.changed.toLocaleString()} changed`
            );
          }}
        />
      ) : null}

      <ClassifyDialog
        items={resubmitItems}
        {...(resubmitTarget ? {resubmitOf: resubmitTarget} : {})}
        classifications={resubmitClassifications ?? []}
        languages={resubmitLanguages ?? []}
        topics={resubmitTopics ?? []}
        locations={resubmitLocations ?? []}
        origin="investigations"
        onClose={() => {
          setResubmitTarget(null);
        }}
        onSubmitted={(_succeeded, linkedItems, wantP0) => {
          setResubmitTarget(null);
          setReloadNonce((n) => n + 1);
          // Same "open for one specific linked item" path the row-level P0AlertsWorkspace launch
          // already uses (see autoOpenLinkedItem above) - reused here rather than p0Dialog.launch,
          // since dialogProps below always renders autoOpenLinkedItem over p0Dialog's own
          // linkedItems when set. A resubmit is single-item only, so linkedItems has exactly one.
          if (wantP0 && linkedItems[0]) {
            setAutoOpenLinkedItem(linkedItems[0]);
            p0Dialog.setOpen(true);
          }
        }}
      />
    </div>
  );
}
